import { useCallback, useEffect, useRef, useState } from 'react';

import Container from '@ui/container/container';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import SessionVodContent from './on-demand/session-vod-content';
import { VideoType } from '@/types/video.types';
import { useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectWorkingProjectDefaultLanguage, selectWorkingSession } from '@/store/slices/project/selectors';
import useGetSessionVideo from './on-demand/use-get-session-video';

import styles from './session-stream-info.module.css';

const SessionVodSection = () => {
  const channel = useAppSelector(getActiveChannel);
  const projectDefaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const session = useAppSelector(selectWorkingSession);

  const [showAddVideoPopover, setShowAddVideoPopover] = useState(false);
  const [selectedVodType, setSelectedVodType] = useState<VideoType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevModalOpenRef = useRef(false);
  const setIsFocusedWithinRef = useRef<((isFocused: boolean) => void) | null>(null);

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const languageKey = projectDefaultLanguage || 'en';
  const videoUuid = session?.static_videos?.[languageKey] ?? null;
  const { sessionVideoData } = useGetSessionVideo(channel, videoUuid, session?.uuid);

  const shouldForceOpen = !videoUuid;

  const handleFocusedWithinChange = useCallback(
    (isFocused: boolean) => {
      if (!isFocused) {
        if (!sessionVideoData) {
          setIsFocusedWithinRef.current?.(true);
          return;
        }
        setShowAddVideoPopover(false);
        setSelectedVodType(sessionVideoData?.type ?? null);
      }
    },
    [sessionVideoData]
  );

  useEffect(() => {
    const wasModalOpen = prevModalOpenRef.current;
    if (wasModalOpen && !isModalOpen) {
      setSelectedVodType(sessionVideoData?.type ?? null);
      setIsFocusedWithinRef.current?.(false);
    }
    prevModalOpenRef.current = isModalOpen;
  }, [isModalOpen, sessionVideoData?.type]);

  return (
    <Container data-component="SessionVodSection">
      <EditStateWrapper
        disabled={!hasPermissions}
        Label="ON DEMAND CONTENT"
        headerClassName="position-absolute width-100-percent"
        className={`${styles['session-stream-info']} position-relative`}
        keepOpen={shouldForceOpen || isModalOpen}
        onFocusedWithinChange={handleFocusedWithinChange}
      >
        {({ isFocusedWithin, setIsFocusedWithin }) => {
          setIsFocusedWithinRef.current = setIsFocusedWithin;

          return (
            <Container
              width="100%"
              height="100%"
              data-show-edit-label={isFocusedWithin ? 'false' : 'true'}
              transitionDuration="0ms"
              className={'margin-t-24'}
            >
              <Container
                width="100%"
                className={`flex jc-flex-end ai-center gap-12 ${!isFocusedWithin ? 'cursor-pointer' : ''} ${styles['content-container']}`}
                hoverBackground={isFocusedWithin ? undefined : 'var(--global-cont-1)'}
                borderRadius="8px"
                transitionDuration={isFocusedWithin ? '0ms' : undefined}
              >
                <SessionVodContent
                  sessionVideo={sessionVideoData}
                  isEditMode={isFocusedWithin}
                  session={session}
                  projectDefaultLanguage={languageKey}
                  selectedType={selectedVodType}
                  onSetSelectedType={setSelectedVodType}
                  onReset={() => setShowAddVideoPopover(true)}
                  showAddPopover={showAddVideoPopover}
                  setShowAddPopover={setShowAddVideoPopover}
                  onModalOpenChange={setIsModalOpen}
                />
              </Container>
            </Container>
          );
        }}
      </EditStateWrapper>
    </Container>
  );
};

export default SessionVodSection;
